import React, { useEffect, useState } from "react";
import { EyeOutlined } from "@ant-design/icons";
import { Button, message } from "antd";
import GenericTable from "../../../Components/Tables/GenericTable";
import { useNavigate } from "react-router-dom";
import { getFleetManagers } from "../../../API/Admin/FleetManagement";
import "./style.css";
import { getInitials } from "../../../Components/InitialsAvatar";

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const formatDate = (isoDate) => {
  if (!isoDate) return "-";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString("en-US");
};

const mapFleetManager = (item) => ({
  key: String(item.id),
  company: item.legal_name || item.company_name || item.name || "-",
  logo: resolveAvatarUrl(item.avatar),
  date: formatDate(item.date_incorporated),
  assignModule:
    Array.isArray(item.assigned_modules) && item.assigned_modules.length
      ? item.assigned_modules.map((mod) => mod.name || mod).join(", ")
      : "-",
  vehicles: item.vehicle_count ?? item.vehicles ?? 0,
  drivers: item.driver_count ?? item.drivers ?? 0,
  creditBalance:
    item.credit_balance != null ? `$${item.credit_balance}` : "-",
  creditUsed: item.credit_used != null ? `$${item.credit_used}` : "-",
  status: item.fueling_locked ? "Inactive" : "Active",
});

const FleetManagement = () => {
  const navigate = useNavigate();

  const [dataSource, setDataSource] = useState([]);

  useEffect(() => {
    const fetchFleetManagers = async () => {
      try {
        const res = await getFleetManagers();
        const managers = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(managers)) {
          setDataSource(managers.map(mapFleetManager));
        } else {
          message.error(res?.data?.message || "Failed to load fleet managers.");
        }
      } catch (err) {
        message.error(
          err?.response?.data?.message || "Failed to load fleet managers.",
        );
      }
    };

    fetchFleetManagers();
  }, []);

  const columns = [
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
      enableSearch: true,
      render: (text, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {record.logo ? (
            <img
              src={record.logo}
              alt={text}
              style={{ width: 24, height: 24, borderRadius: "5px" }}
            />
          ) : (
            <span
              style={{
                width: 24,
                height: 24,
                borderRadius: "5px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 700,
                color: "#fff",
                background: getAvatarColor(text),
                flexShrink: 0,
              }}
            >
              {getInitials(text)}
            </span>
          )}
          {text}
        </span>
      ),
    },
    {
      title: "Date Incorporated",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) =>
        (new Date(a.date).getTime() || 0) - (new Date(b.date).getTime() || 0),
      render: (text) => <span className="reg-date-cell">{text}</span>,
    },
    {
      title: "Vehicles",
      dataIndex: "vehicles",
      key: "vehicles",
      sorter: (a, b) => a.vehicles - b.vehicles,
    },
    {
      title: "Drivers",
      dataIndex: "drivers",
      key: "drivers",
      sorter: (a, b) => a.drivers - b.drivers,
    },
    {
      title: "Credit Balance",
      dataIndex: "creditBalance",
      key: "creditBalance",
      sorter: (a, b) =>
        (parseFloat(a.creditBalance.replace(/[^0-9.-]+/g, "")) || 0) -
        (parseFloat(b.creditBalance.replace(/[^0-9.-]+/g, "")) || 0),
    },
    {
      title: "Credit Used",
      dataIndex: "creditUsed",
      key: "creditUsed",
      sorter: (a, b) =>
        (parseFloat(a.creditUsed.replace(/[$,]/g, "")) || 0) -
        (parseFloat(b.creditUsed.replace(/[$,]/g, "")) || 0),
      render: (text) => <span className="credit-used-text">{text}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        ...new Set(dataSource.map((item) => item.status).filter(Boolean)),
      ].map((status) => ({ text: status, value: status })),
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <span
          className={`status-badge ${status === "Active" ? "active" : "locked"}`}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined style={{ fontSize: 18, color: "#4F46E5" }} />}
          onClick={() =>
            navigate("/fleet-management-detail", { state: { record } })
          }
        />
      ),
    },
  ];

  return (
    <div className="fleet-table">
      <GenericTable
        title="Fleet Managers"
        dataSource={dataSource}
        columns={columns}
        enableRowSelection={false}
      />
    </div>
  );
};

export default FleetManagement;
